import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoading } from '../../contexts/LoadingContext';
import { truncateString } from '../../lib/common';
import { cn } from '../../lib/utils';
import { useGetUserConversations } from '../../hooks/db/useGetUserConversations';
import { useDeleteConversation } from '../../hooks/db/useDeleteConversation';

const ChatHistory: React.FC = () => {
    const { getConversations } = useGetUserConversations()
    const { deleteConversation } = useDeleteConversation()
    const { setLoading } = useLoading()
    
    const navigate = useNavigate()
    const [searchQuery] = useSearchParams()

    const [conversations, setConversations] = useState<Record<string, any>[]>([])
    const [selectedConversation, setSelectedConversation] = useState<Record<string, any> | null>(null)
    const [showLoadMorebtn, setShowLoadMorebtn] = useState(false)
    const [isPageLoading, setIsPageLoading] = useState(false)

    const [totalCount, setTotalCount] = useState(0)
    const pageSize = 20
    const [currentPage, setCurrentPage] = useState(1)

    const c = searchQuery.get('c') // get the conversation id from URL params

    const handleView = (conversation: any) => {
        setSelectedConversation(conversation)
        navigate(`/research?c=${conversation._id}`)
    }

    const fetchConversations = async (page: number) => {
        setIsPageLoading(true)
        try {
            let filters = {}
            const result = await getConversations(page, pageSize, filters)
            if (result && 'conversations' in result) {
                setConversations((result as { conversations: any }).conversations)
                setTotalCount(result.totalCount)
                if (result.totalCount > pageSize) {
                    setShowLoadMorebtn(true)
                }
            }else{
                setShowLoadMorebtn(false)
            }
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log("Fetch aborted");
            } else {
                console.error("Error fetching conversations:", error);
            }
                toast.error("Error fetching conversations", {
                position: "bottom-center",
                })
        }finally{
            setIsPageLoading(false)
        }
    }

    useEffect(() => {
        if(currentPage === 1){ // only for initial load, for further loading, we use the load more handler
            fetchConversations(currentPage)
        }
    }, [currentPage])

    const handleLoadMore = async () => {
        setIsPageLoading(true)
        try {
            const nextPage = currentPage + 1            
            const result = await getConversations(nextPage, pageSize, {})
            if (result && 'conversations' in result) {
                setConversations((prev) => {
                    const newConversations = (result as { conversations: any }).conversations;
                    return [...prev, ...newConversations]
                })
                setCurrentPage(nextPage)
                setTotalCount(result.totalCount)
                if ((result as { conversations: any }).conversations.length > pageSize) {
                    setShowLoadMorebtn(true)
                }else{
                    setShowLoadMorebtn(false)
                }
            }
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log("Fetch aborted");
            } else {
                console.error("Error loading more conversations:", error);          
            }
            toast.error("Error loading more conversations", {
                position: "bottom-center",
            })
        } finally {
            setIsPageLoading(false)
        }
    }

    const handleDeleteConversation = async (conversationId: string) => {
        const userConfirmed = window.confirm("Are you sure you want to delete this conversation? This action cannot be undone.")
        if(userConfirmed) {
            
            setLoading(true)
            try {
                const result = await deleteConversation(conversationId)
                console.log("Delete Conversation Response:", result)
                if (result && result.success) {
                    setConversations((prev) => prev.filter((conv) => conv._id !== conversationId))
                    toast.success("Conversation deleted successfully", {
                        position: "bottom-center",
                    });
                } else {
                    toast.error("Failed to delete conversation", {
                        position: "bottom-center",
                    });
                }
            } catch (error) {       
                console.error("Error deleting conversation:", error);
                toast.error("Error deleting conversation", {
                    position: "bottom-center",
                });
            }
            finally {
                setLoading(false)
            }
        }else{
            return // User cancelled the deletion
        }
    }


    return (
        <div className="flex flex-col md:flex-row w-full">
            <div className={`overflow-auto w-full`}>
                <div className="overflow-x-auto cursor-pointer chat-history-scroller mb-3">
                    {conversations.map((row, index) => (
                        <div
                            className={cn(
                                        'flex items-center justify-between px-4 py-2 pr-1 hover:bg-gray-800 transition',
                                        c && c === row._id && 'bg-gray-800 '
                                    )}
                            onClick={() => handleView(row)}
                            key={index}
                            title={row.title}
                        >
                            <span>{truncateString(row.title, 25)}</span>
                            <button
                                title={'Delete Conversation'}
                                aria-label="Delete Conversation"
                                className="ml-1 px-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the handleView
                                    handleDeleteConversation(row._id)
                                }}
                            >
                               x
                            </button>
                        </div>
                    ))}
                    
                </div>
                {isPageLoading && (
                    <div className="flex justify-center items-center h-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                )}
                {showLoadMorebtn && (
                    <div className='mt-4 mb-10 flex justify-center'>
                        {/* Load more button */}
                        <button 
                            className='px-4 py-1 bg-gray-600 text-white rounded hover:bg-gray-500 transition'
                            onClick={handleLoadMore}
                            disabled={isPageLoading}
                            title="Load more conversations"
                        >
                            Load More
                        </button>
                    </div>
                )}
                <div>
                    {!isPageLoading && conversations.length === 0 && (
                        <div className="text-center text-gray-500 mt-4">
                            No conversations found.
                        </div>
                    )}
                </div>
            </div>
        </div>

      )
      
}

export default ChatHistory